import {BaseApi} from "@/core/api/base.api";
import {RoomCall} from "@/core/api/room.call.api";
import {Room} from "@/core/api/room.api";
import {GrmWebRTC} from "@/lib/gooroomee/webrtc/GrmWebRTC";
import {roomCallService} from "@/core/service/room.call.service";
import {RoomCallLog} from "@/core/api/room.call.log.api";
import { cloneDeep, debounce } from 'lodash';
import {Device} from "@/utils";

const CALL_TYPE = 'p2p';
const CONFIG = {
    // mid: {
    //     primary: {
    //         audio: '0',
    //         video: '1',
    //     },
    //     datachannel: '2',
    //     secondary: {
    //         audio: '3',
    //         video: '4',
    //     }
    // },
    rtpConfig: {
        video: {
            send: {
                // maxBitrate: 512,
                // maxFramerate: 15
                maxBitrate: 0,
                maxFramerate: 15
            }
        }
    },
    device: {
        defaultConstraints: {
            video: {
                width: 320,
                height: 180,
                frameRate: 15
            },
            audio: {}
        }
    },
    videoMimeTypes: null
};

class RoomCallP2PApi extends BaseApi {

    constructor() {
        super();
        RoomCall.P2P = this;
        RoomCall.addCallTypeEventListener(CALL_TYPE, this._Events, this);

        Room.addEventListeners(this._CoreEvents, this);

        Room.addEventListener('room_core_call_infoLoaded', async e => {
            const {callType} = Room.getRoomData().roomCall;
            if (callType !== CALL_TYPE) {
                return;
            }
            await this.createStream();
        });
    }

    destroy() {
        const roomUserList = Room.User.getRoomUserList();
        console.log('grm p2p api destroy : userLen[%d]', roomUserList.length);

        roomUserList.forEach(ru => {
            if (!ru.conn) return;
            ru.conn.close();
            delete ru.conn;
        });
    }

    async createStream() {
        if(Room.User.isGRMAFCD()){
            await RoomCall.Stream.removeCurrentLocalStream();
            return;
        }

        if (Device.isSafari()) {
            // 2023.05.30 mac-10.15, safari-16.4 기준 15 초과된 값을 설정하면 무조건 30 프레임으로 송출
            CONFIG.device.defaultConstraints.video.frameRate = 15;
        }

        let config = cloneDeep(CONFIG.device.defaultConstraints);
        await (async () => {
            try{
                let settings = RoomCall.Stream.getLocalDeviceSetting();
                let deviceId = settings.video?.deviceId;
                if(deviceId){
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const device = devices.find(device => device.deviceId === deviceId);
                    if(!device){
                        let device = devices.find(d => d.kind === 'videoinput');
                        if(device){
                            deviceId = device.deviceId;
                            settings.video.deviceId = deviceId;
                            RoomCall.Stream.setLocalDeviceSetting(settings);
                        }else{
                            deviceId = null;
                        }
                    }
                    if(deviceId){
                        config.video.deviceId = {
                            exact: deviceId
                        }
                    }
                }
            }catch(e){
                console.error(e);
            }
        })();
        try {
            Room.trigger('room_call_loadLocalStreamStart')
                .catch(e => console.error(e));
            let stream = await RoomCall.Stream.createLocalStream(config);

            if(stream.getVideoTracks().length > 0){
                const track = stream.getVideoTracks()[0];
                RoomCall.addUserStreamTrack(
                  Room.User.getMe().userId,
                  'video',
                  track
                );
                const grmTrackGen = track.GrmTrackUtils()?.getTrackGenerator();
                if(grmTrackGen){
                    const videoMirror = RoomCall.Stream.getVideoMirror()
                    grmTrackGen.setMirror?.(videoMirror);
                    let setSize = () => {
                        let {width, height} = track.getSettings();
                        if(!width){
                            console.error('# size error:', width, height, track.muted, Date.now())
                            if(!track.muted && !track.ended){
                                setTimeout(() => {
                                    setSize();
                                }, 10);
                                return;
                            }
                        }
                        let size = Math.min(width, height);
                        grmTrackGen.resize(size, size, 'crop')
                        if(Device.isSafari()){
                            grmTrackGen.changeFPS(5)
                        }
                    };
                    setSize();
                }
            }

            if (stream.getAudioTracks().length > 0) {
                RoomCall.addUserStreamTrack(
                  Room.User.getMe().userId,
                  'audio',
                  stream.getAudioTracks()[0]
                );
                RoomCall.setAudioTrackVolumeEvent({
                    userId: Room.getRoomData().me.userId,
                    stream: stream
                });
            } else { // 권한이 없는 등의 이유로 audio track이 생성되지 경우
            }

            Room.trigger('room_call_loadLocalStream', {
                // stream: streamObj.previewStream || streamObj.stream}
                stream: stream
            }).catch(e => console.error(e));
        } catch (e) {
            console.error('#[grm-call] create localstream error', config, e);
        }
    }

    async createWebRTCConnection (roomUser, options = {}) {
        roomUser = Room.User.getRoomUser(roomUser.userId);
        const {userId, nickname} = roomUser;

        console.log('create peerConnection : user[%s/%s]',
            userId, nickname
        );

        let conn = roomUser.conn;
        if (!conn || conn.isClosed()) {
            try {
                conn && conn.close();
            } catch (e) {
                console.error(e);
            }
        }


        // let roomOptions = Room.getRoomData().roomOptions;
        let config = {};

        options.iceServers = (() => {

            const list = [];
            let stunCnt = process.env[`VUE_APP_ICE_STUN_SERVER_COUNT`];
            for(let i=1; i<=stunCnt; i++){
                const urls = process.env[`VUE_APP_ICE_STUN_SERVER${i}_URL`];
                if(!urls) break;
                list.push({urls});
            }
            let turnCnt = process.env[`VUE_APP_ICE_TURN_SERVER_COUNT`];
            for(let i=1; i<=turnCnt; i++){
                const urls = process.env[`VUE_APP_ICE_TURN_SERVER${i}_URL`];
                if(!urls) break;
                list.push({
                    urls,
                    username: process.env[`VUE_APP_ICE_TURN_SERVER${i}_USERNAME`],
                    credential: process.env[`VUE_APP_ICE_TURN_SERVER${i}_CREDENTIAL`]
                });
            }
            return list;
        })();
        if(options.iceServers.length === 0){
            options.iceServers = [
                    {'urls': 'stun:stun.l.google.com:19302'},
                    {'urls': 'stun:hq.gooroomee.com'},
                    {urls: 'turn:hq.gooroomee.com:20000', username: 'gooroomee', credential: 'grm0909'},
                    {urls: 'turn:grmdev-main.gooroomee.com', username: 'gooroomee', credential: 'grm0909'}
            ];
        }
        // [
        //     {urls: process.env.VUE_APP_ICE_STUN_SERVER1_URL},
        //     {urls: process.env.VUE_APP_ICE_STUN_SERVER2_URL},
        //     {urls: process.env.VUE_APP_ICE_TURN_SERVER1_URL, username: process.env.VUE_APP_ICE_TURN_SERVER1_USERNAME, credential: process.env.VUE_APP_ICE_TURN_SERVER1_CREDENTIAL},
        //     {urls: process.env.VUE_APP_ICE_TURN_SERVER2_URL, username: process.env.VUE_APP_ICE_TURN_SERVER2_USERNAME, credential: process.env.VUE_APP_ICE_TURN_SERVER2_CREDENTIAL},
        // ];

        // options.iceTransportPolicy = 'relay';

        roomUser._c_data = roomUser._c_data || {
            bw: {
                video: Object.assign({}, CONFIG.rtpConfig.video.send),
                secondVideo: Object.assign({}, CONFIG.rtpConfig.video.send)
            }
        };
        conn = roomUser.conn = new GrmWebRTC(options);
        Object.assign(conn, {
            streams: {
                video: new MediaStream(),
                second: new MediaStream()
            },
        });

        conn.createPeerConnection(config, options);
        // conn.getPeerConnection()._bw = {video: -1, second: -1};

        conn.addEventListener('addTransceiver', e => {
            const {transceiver, kind, index} = e;
            try{
                if(kind === 'video'){
                    let mimeTypes = ['video/H264', 'video/VP9', 'video/VP8'];
                    if(CONFIG.videoMimeTypes){
                        mimeTypes = CONFIG.videoMimeTypes;
                    }

                    // mimeTypes.push('video/H264');
                    // if(Device.isSafari()){
                    //     if(
                    //         !Device.isBrowserAvailable('safari', 16)
                    //         || parseFloat(Device.osVersion()) < 16
                    //     ){
                    //         mimeTypes = ['video/H264', 'video/VP8'];
                    //     }
                    // }else if(roomUser.clientName?.includes('afari')){
                    //     if(roomUser.osVersion && parseFloat(roomUser.osVersion) < 16){
                    //         mimeTypes = ['video/H264', 'video/VP8'];
                    //     }
                    // }else if(roomUser.deviceInfo){
                    //     try{
                    //         let device = roomUser.deviceInfo.toLowerCase();
                    //         if(device.includes('safari') && device.endsWith(')')){
                    //             let version = device.substring(device.indexOf('(')+1, device.length-1);
                    //             version = parseFloat(version);
                    //             if(version < 16){
                    //                 mimeTypes = ['video/H264', 'video/VP8'];
                    //             }
                    //         }
                    //     }catch(e){
                    //         console.error(e);
                    //     }
                    // }

                    // mimeTypes = ['video/H264', 'video/VP9', 'video/VP8'];
                    // if(Device.isAndroid()){
                    //     let ver = parseFloat( Device.osVersion() );
                    //     if(ver < 11){
                    //         mimeTypes = ['video/VP8', 'video/VP9', 'video/H264'];
                    //     }
                    // }
                    conn.setVideoCodec({
                        transceiver,
                        mimeTypes
                    })
                }
            }catch(e){
                console.error(e);
            }
        });

        let stream = await RoomCall.Stream.getCurrentLocalStream();
        if (stream) {
            const addTrack = (kind, track) => {
                if(track){
                    conn.addTrack(track);
                }else if(options.offer && !conn.getTransceiverByKind(kind)){
                    conn.addTransceiver(kind);
                }
            };
            if(stream.getAudioTracks()[0]){
                addTrack('audio', stream.getAudioTracks()[0]);
            }
            if(stream.getVideoTracks()[0]){
                addTrack('video', stream.getVideoTracks()[0]);
            }
        }

        function afterAddEventListener() {
            if (options.offer) {
                try {
                    conn.createDataChannel('grm_datachannel');
                    // RoomDataChannel.setDataChannel(THIS, roomUser, conn, dataChannel);
                } catch (e) {
                    console.error(e);
                }

                // ['audio', 'video'].forEach(kind => {
                //     if(!conn.getTransceiverByKind('kind', 0)){
                //         conn.addTransceiver(kind);
                //     }
                // });
                //
                // const screenStream = RoomCall.Stream.getCurrentScreenStream();
                // if (screenStream) {
                //     ['audio', 'video'].forEach(kind => {
                //         if(!conn.getTransceiverByKind('kind', 1)){
                //             conn.addTransceiver(kind);
                //         }
                //     });
                // }
            }

            // Room.emit('room_core_call_userConnectionCreated_'+userId, {userId, roomUser});
        }

        conn.__init__ = true;
        conn
            .addEventListener('negotiationneeded', async e => {
                console.debug('# peer.%s : user[%s/%s]',
                    e.type,
                    userId, nickname);

                await this.sendOffer({userId});
            })
            .addEventListener('addstream', e => {
                let stream = e.stream;

                console.log('%s roomUser : user[%s/%s], id[%s], v[%d], a[%d]',
                    e.type
                    , userId, nickname
                    , stream.id
                    , stream.getVideoTracks().length
                    , stream.getAudioTracks().length
                );

                // Room.emit(eventType, {stream});
            })
            .addEventListener('track', e => {
                console.log('# peer.%s roomUser : user[%s/%s], id[%s], k[%s]',
                    e.type
                    , userId, nickname
                    , e.track.id
                    , e.track.kind
                    , e
                );

                const {track} = e;
                const firstTrans = conn.getTransceiverByKind(track.kind);
                const secondTrans = conn.getTransceiverByKind(track.kind, 1);

                const streams = RoomCall.getUserStreams(roomUser.userId);
                if(e.transceiver === firstTrans){
                    conn.streams.video.addTrack(track);
                    Room.emit('room_call_userVideoStream', {userId, stream: conn.streams.video});

                    if (track.kind === 'video') {
                        streams.video.getVideoTracks().forEach(t => {
                            streams.video.removeTrack(t);
                        });
                        streams.video.addTrack(track);
                    } else if (track.kind === 'audio') {
                        streams.audio.getAudioTracks().forEach(t => {
                            streams.audio.removeTrack(t);
                        });
                        streams.audio.addTrack(track);

                        Room.emit('room_call_addUserAudioTrack', {userId, track});
                        RoomCall.setAudioTrackVolumeEvent({
                            userId,
                            track
                        });
                    }
                }else if(e.transceiver === secondTrans){
                    conn.streams.second.addTrack(track);
                    Room.emit('room_call_userSecondStream', {userId, stream: conn.streams.second});
                }
            })
            .addEventListener('removestream', e => {
                let stream = e.stream;
            })
            .addEventListener('icecandidate', async e => {
                if (!e.candidate) {
                    return;
                }

                // if(e.candidate.type === 'relay'){
                //     if(conn.__init__){
                //         return;
                //     }
                // }
                await roomCallService.sendIcecandidate({
                    callType: CALL_TYPE,
                    toUserId: userId,
                    iceCandidate: e.candidate
                });
            })
            .addEventListener('iceconnectionstatechange', e => {
                let state = e.target.iceConnectionState;

                if(conn.restartIceTimer) {
                    clearTimeout(conn.restartIceTimer)
                }
                const restartIce = (state, timeout) => {
                    delete conn.__init__;
                    try {
                        console.log(`restart Ice: iceConnectionState[${state}],timeout[${timeout}ms]`)
                        conn?.restartIce()
                    } catch (e) {
                        console.warn('restartIceTimer exception ', state, timeout, e)
                    }
                }

                switch (state) {
                    case 'checking': {
                        console.log('pc ice statechange : %s,', state, userId, nickname);
                        if(RoomCall.isP2P()) {
                            conn.restartIceTimer = setTimeout(() => {
                                restartIce(state, 5 * 1000)
                            }, 5 * 1000)
                        }
                        break;
                    }
                    case 'connected': {
                        delete conn.__init__;
                        console.log('pc ice statechange : %s,', state, userId, nickname);
                        Room.emit('room_call_userConnectionConnected', {userId});

                        this.connectionStats({roomUser, start: true});
                        break;
                    }
                    case 'disconnected':
                    case 'closed': {
                        delete conn.__init__;
                        if (RoomCall.isP2P()) {
                            console.error('pc ice statechange : %s,', state, userId, nickname, e);
                            Room.emit('room_call_userConnectionDisconnected', {userId});

                            if(state === 'disconnected') {
                                conn.restartIceTimer = setTimeout(() => {
                                    restartIce(state, 3 * 1000)
                                }, 3 * 1000)
                            }
                        }
                        this.connectionStats({roomUser, start: false});
                        break;
                    }
                    case 'failed': {
                        delete conn.__init__;
                        console.error('pc ice statechange : %s,', state, userId, nickname, e);
                        Room.emit('room_call_userConnectionFailed', {userId});

                        this.connectionStats({roomUser, start: false});

                        if(RoomCall.isP2P()) {
                            conn.restartIceTimer = setTimeout(() => {
                                restartIce(state, 1 * 1000)
                            }, 1 * 1000)
                        }
                        break;
                    }
                    default:
                        console.log('pc ice statechange : %s,', state, userId, nickname);
                        break;
                }
            })
            .addEventListener('signalingstatechange', e => {
                let state = e.target.signalingState;
                switch (state) {
                    case 'stable':
                        roomCallService.sendSignalStable({toUserId: userId});
                        break;
                    case 'closed':
                        break;
                }
            })
            .addEventListener('datachannel', e => {
                const dataChannel = e.channel;
                let label = dataChannel.label;

                conn.defaultDataChannel = dataChannel;
                dataChannel.onerror = e => {
                    console.log('#[grm-webrtc] dc[%s] error :', label, userId, e);
                };
                dataChannel.onopen = e => {
                    console.log('#[grm-webrtc] dc[%s] open :', label, userId);
                    Room.emit('room_core_call_userConnectionCreated_'+userId, {userId, roomUser});
                };
                dataChannel.onclose = e => {
                    console.log('#[grm-webrtc] dc[%s] close : %s, bufferedAmount[%d], bufferedAmountLowThreshold[%d]',
                        label,
                        userId,
                        dataChannel.bufferedAmount, dataChannel.bufferedAmountLowThreshold,
                        e);
                };

                // let buffer;
                dataChannel.onmessage = event => {
                    const dataType = typeof (event.data);

                    (() => {
                        if (dataType !== 'string') {
                            return;
                        }

                        const msg = JSON.parse(event.data);
                        const eventType = msg.type || msg.api;

                        switch (eventType) {
                            default:
                                msg.userId = roomUser.userId;
                                msg.user = roomUser.user;
                                msg._fromDatachannel = true;

                                console.log('on user datachannel msg :', userId, msg);

                                this._Events[eventType](msg);
                                break;
                        }
                    })();
                }
            });

        afterAddEventListener();
        return conn;
    }

    async _setStream(conn) {
        if (!conn) {
            return;
        }

        const jobs = [];

        // add videoStream
        await (async () => {
            let stream = await RoomCall.Stream.getCurrentLocalStream();
            if (!stream) {
                return;
            }
            const setTrack = track => {
                if(!track) return;
                let trans = conn.getTransceiverByKind(track.kind);
                let job;
                if(trans){
                    job = conn.replaceTrackByTransceiver(trans, track);
                }else{
                    trans = conn.addTransceiver(track.kind);
                    job = Promise.resolve();
                }
                jobs.push(job);
                return trans;
            };
            setTrack(stream.getAudioTracks()[0]);
            setTrack(stream.getVideoTracks()[0]);
        })();

        // add screenStream
        (() => {
            const screenStream = RoomCall.Stream.getCurrentScreenStream();
            if (!screenStream) {
                return;
            }
            const getSecondTrans = kind => {
                let trans = conn.getTransceiverByKind(kind, 1);
                if(!trans){
                    trans = conn.addTransceiver(kind);
                }
                return trans;
            }
            const audioTrans = getSecondTrans('audio');
            const videoTrans = getSecondTrans('video');
            if (screenStream.getAudioTracks().length > 0) {
                jobs.push(
                  conn.replaceTrackByTransceiver(
                    audioTrans,
                    screenStream.getAudioTracks()[0]
                  )
                );
            }
            if (screenStream.getVideoTracks().length > 0) {
                jobs.push(
                  conn.replaceTrackByTransceiver(
                    videoTrans,
                    screenStream.getVideoTracks()[0]
                  )
                );
            }
        })();
        await Promise.all(jobs);
    }

    async sendOffer ({roomUser, userId}) {
        roomUser = roomUser || Room.User.getRoomUser(userId);
        if (!roomUser) {
            return;
        }

        let conn = roomUser.conn;

        let localSdp = await conn.createOffer();
        localSdp = await conn.setLocalDescription(localSdp);

        if (!conn.getPeerConnection().remoteDescription) {
            const trans = conn.getTransceiverByKind('video');
            if(trans && trans.sender){
                conn.changeSenderRTCRtpEncodingParameters(
                  trans.sender,
                  roomUser._c_data.bw.video
                ).catch(e => console.error(e));
            }
        }
        await this._setStream(conn).catch(e => console.error(e));

        let data = {
            callType: CALL_TYPE,
            toUserId: roomUser.userId,
            sdp: localSdp,
        };
        await roomCallService.sendOffer(data);
    }

    async sendAnswer ({roomUser, userId, sdp, renew}) {
        roomUser = roomUser || Room.User.getRoomUser(userId);

        let conn = roomUser.conn;
        if (conn && (renew || conn.isClosed())) {
            conn.close();
            conn = null;
        }

        const created = !conn;
        if (created) {
            conn = roomUser.conn = await this.createWebRTCConnection(roomUser);
        }

        let localSdp = await conn.createAnswer(sdp);
        localSdp = await conn.setLocalDescription(localSdp);

        if (created) {
            const trans = conn.getTransceiverByKind('video');
            if(trans && trans.sender){
                conn.changeSenderRTCRtpEncodingParameters(
                  trans.sender,
                  roomUser._c_data.bw.video
                ).catch(e => console.error(e));
            }
        }
        await this._setStream(conn).catch(e => console.error(e));

        // const peer = conn.getPeerConnection();
        // if(!peer.canTrickleIceCandidates){
        //     localSdp = await new Promise((resolve, reject) => {
        //         peer.addEventListener('icegatheringstatechange', e => {
        //             if(e.target.iceGatheringState === 'complete'){
        //                 resolve(e.target.localDescription);
        //             }
        //         }, {once: true});
        //     });
        // }

        // send answer
        let data = {
            callType: CALL_TYPE,
            toUserId: roomUser.userId,
            sdp: localSdp
        }
        await roomCallService.sendAnswer(data);
    }

    async changeVideoSenderMaxBitrate ({userId, videoWidth, videoHeight, bitrate, framerate, degradationPreference, seq}) {
        if (!userId || Room.User.isMe(userId)) {
            return;
        }
        // console.trace('@@@@ changeVideoSenderMaxBitrate:', {userId, videoWidth, videoHeight, bitrate, framerate, degradationPreference});
        if(Room.User.isGRMAFCD()){
            if(bitrate > 1){
                bitrate = parseInt(bitrate / 2);
            }
            if(framerate > 1){
                framerate = parseInt(framerate / 2);
            }
        }
        const roomUser = Room.User.getRoomUser(userId);
        if (!roomUser) {
            console.log('roomUser is null :', userId);
            return;
        }

        if(seq === undefined || seq === null){
            seq = parseInt(performance.now() * 1000000);
        }

        if(!roomUser.conn){
            console.log('roomUser conn not yet :', userId);
            Room.addEventListener('room_core_call_userConnectionCreated_'+userId, e => {
                if(e.data.roomUser !== roomUser){
                    // return;
                }
                this.changeVideoSenderMaxBitrate({userId, videoWidth, videoHeight, bitrate, framerate, degradationPreference, seq});
            }, {once: true});
            return;
        }

        const pc = roomUser.conn.getPeerConnection();
        if (!pc) {
            return;
        }

        await(async () => {
            if(bitrate <= 10) return;
            const codec = (await roomUser.conn.getCodecInfo()).receive?.find(c => c.mimeType==='video/VP9');
            if(codec){
                bitrate = Math.max(bitrate * 0.7, 40);
            }
        })();

        const send = ((roomUser, {videoWidth, videoHeight, bitrate, framerate, degradationPreference, seq}) => {
            const cn = roomUser.conn.defaultDataChannel;
            if(!cn.__send_bw_init){
                setTimeout(() => {
                    cn.__send_bw_init = true;
                    send();
                }, 300);
                return;
            }

            cn._sendBw_debounce
              = cn._sendBw_debounce
              || debounce((videoWidth, videoHeight, bitrate, framerate, degradationPreference, seq)=>{
                  cn.sendQueue(JSON.stringify({
                      type: 'room_call_changeVideoSenderMaxBitrate',
                      data: { videoWidth, videoHeight, bitrate, framerate, degradationPreference, seq }
                  }));
                }, 500);
            cn._sendBw_debounce(videoWidth, videoHeight, bitrate, framerate, degradationPreference, seq);
        }).bind(this, roomUser, {videoWidth, videoHeight, bitrate, framerate, degradationPreference, seq});

        if (!roomUser.conn.defaultDataChannel) {
            const onDataChannel = ((userId, roomUser, send) => {
                console.warn('datachannel not yet', userId, roomUser.conn);
                roomUser.conn.addOnceEventListener('datachannel', (e) => {
                    if (!roomUser.conn.defaultDataChannel) {
                        onDataChannel();
                        return;
                    }
                    e.channel.addEventListener('open', () => {
                        send();
                    }, {once: true})
                })
            }).bind(this, userId, roomUser, send);
            onDataChannel();
            return;
        }
        send();
    }

    async changeSecondVideoSenderMaxBitrate({userId, bitrate, framerate}) {
        const roomUser = Room.User.getRoomUser(userId);
        if (!roomUser || !roomUser.conn) {
            console.log('roomUser is null :', roomUser);
            return;
        }

        const pc = roomUser.conn.getPeerConnection();
        if (!pc) {
            return;
        }

        await(async () => {
            if(bitrate < 10) return;
            const codec = (await roomUser.conn.getCodecInfo()).receive?.find(c => c.mimeType==='video/VP9');
            if(codec){
                bitrate = Math.max(bitrate * 0.8, 40);
            }
        })();

        roomUser.conn.defaultDataChannel.sendQueue(JSON.stringify({
            type: 'room_call_changeSecondVideoSenderMaxBitrate',
            data: {bitrate, framerate}
        }))
    }

    getUserCurrentConnectionStats (userId) {
        const roomUser = Room.User.getRoomUser(userId);
        if(!roomUser) return null;
        const {connectionStatsResult} = roomUser;
        if(!connectionStatsResult) return null;
        return {...connectionStatsResult};
    }

    connectionStats({roomUser, start}) {
        if (!roomUser) {
            return;
        }
        const {userId} = roomUser;
        if (roomUser.connectionStatsResult && roomUser.connectionStatsResult.stop) {
            roomUser.connectionStatsResult.stop();
        }

        if(!roomUser.conn){
            return;
        }
        if(start && roomUser.conn._connectionStats_start){
            return;
        }
        roomUser.conn._connectionStats_start = start;
        if (!start) {
            return;
        }

        Room.emit('startRoomUserConnectionStats', {userId});
        const conn = roomUser.conn;
        let chkAudio = 0;
        let chkVideoReceiveErr = 0;
        conn.getPeerConnection().getCsStats({
            peerId: roomUser.userId,
            intervalMs: 4000,
            resultCallback: (result) => {
                roomUser.connectionStatsResult = result;

                if (conn.isClosed()) {
                    result.stop();
                    return;
                }

                let videoDecodeError = (() => {
                    if(roomUser.cameraStatus !== 'on'){
                        return;
                    }
                    let interval = result.intervalMs / 1000;
                    const receive = result.video?.receive;
                    if(!receive) return;
                    const bwKbps = receive.availableBandwidthKbps / interval;
                    if(bwKbps === 0){
                        return;
                    }
                    const stream = receive.streams[0];
                    if(!stream) return;
                    if(stream.width > 0 && stream.height > 0 && stream.instantFramesDecoded > 0){
                        return;
                    }
                    chkVideoReceiveErr++;
                    if(chkVideoReceiveErr > 10){
                        chkVideoReceiveErr = 0;
                        CONFIG.videoMimeTypes = ['video/VP8', 'video/VP9', 'video/H264'];
                        conn.close();
                        RoomCall.requestOffer(roomUser.userId, 'videoDecodeError');
                        return true;
                    }
                    return true;
                })();
                if(videoDecodeError !== true){
                    chkVideoReceiveErr = 0;
                }

                try{
                    Room.emit('userConnectionStats', {roomUser, result});
                }catch(e){
                    console.warn(e);
                }


                /*
                android 기기 중 오디오 패킷이 수신되는데 재생이 안되는 케이스 발생. (보통 간헐적 발생. 특정 기기에서 10회중 1~2번 발생)
                다른 로직을 전부 제거 해도 재현되지 않고 pc 를 재연결 해야 해결됨. (일단 이렇게 결론)
                오디오 패킷이 발생하는데 audio track volume 이 발생하지 않는다면 2회까지 재연결 시도 한다.
                track 이 바뀌는 케이스는 고려되지 않음.
                 */
                (() => {
                    // check 완료
                    if(chkAudio === true) return;
                    // 마이크 on
                    if(roomUser.micStatus !== 'on' || roomUser.roomUserRole?.mic !== true){
                        return;
                    }
                    // 오디오 수신 패킷
                    if(chkAudio !== true && result.audio?.receive?.instantPacketsReceived <= 0){
                        return;
                    }
                    // 수신 audio track
                    const trans = conn.getPeerConnection().getTransceivers().find(trans => {
                        return trans.receiver?.track?.kind === 'audio';
                    });
                    if(!trans){
                        return;
                    }

                    const track = trans.receiver.track
                    // _grm_soundMeter 가 있어야 _grm_volume 값이 셋팅됨.
                    if(!track._grm_soundMeter) return;
                    let active = track._grm_active;
                    // active 라면 pass
                    if(active > 0){
                        chkAudio = true;
                        return;
                    }

                    // 수신 패킷은 있는데 volume 이 발생하지 않음.
                    chkAudio++;
                    if(chkAudio > 2){
                        console.warn('audio can not play : loop[%d], active[%d]', chkAudio, active, trans.receiver.track);
                        roomUser._grm_err_audio_v_reconn = roomUser._grm_err_audio_v_reconn || 0;
                        roomUser._grm_err_audio_v_reconn++;

                        if(roomUser._grm_err_audio_v_reconn > 3){
                            // limit 까지만 재시도 한다.
                            return;
                        }

                        // pc close 하고 재연결
                        conn.close();
                        RoomCall.requestOffer(userId, 'audioTrackPlayError');
                        return;
                    }
                })();

                let roomData = Room.getRoomData();
                let me = roomData.me;
                RoomCallLog.sendPeerStatsLog({
                    roomId: roomData.room.roomId,
                    userId: me.userId,
                    username: me.nickname,
                    remoteUserId: userId,
                    remoteUsername: 'mixer',
                    ipAddr: (me.userSession && me.userSession.ipAddr) || '',
                    connectionType: result.connectionType,
                    video: result.video,
                    audio: result.audio,
                    codecs: result.codecs,
                    intervalMs: result.intervalMs,
                    current: conn.current,
                    connTime: Date.now() - conn.getPeerConnection()._connectedTime,
                });
            },
            stopCallback: (code, cause) => {
                console.debug("CsStats stoped", code, cause);

                this._connectionStats_start = false;
                Room.emit('stopUserConnectionStats', {roomUser, userId: roomUser.userId});
            }
        });
    }

    async startScreenShare(stream, callback) {
        callback(stream);

        Room.User.getRoomUserList().forEach(roomUser => {
            this._setStream(roomUser.conn);
        });
    }

    stopScreenShare() {
        const screenStream = RoomCall.Stream.getCurrentScreenStream();
        if(!screenStream) return;
        const tracks = screenStream.getTracks();

        RoomCall.Stream.removeScreenStream();
        Room.User.getRoomUserList().forEach(roomUser => {
            if (!roomUser.conn) {
                return;
            }
            tracks.forEach(track => {
                roomUser.conn.replaceTrackBySenderTrack(track, null);
            });
        });
    }

    getUserStream({userId, mids}) {
        const roomUser = Room.User.getRoomUser(userId);
        if (!roomUser || !roomUser.conn) {
            return;
        }

        const pc = roomUser.conn.getPeerConnection();
        if (!pc) {
            return;
        }

        const arr = mids.reduce((arr, mid) => {
            let track = pc.getTransceivers().find(t => t.mid === mid)?.receiver?.track;
            arr.push(track);
            return arr;
        }, []);
        return arr;
    }

    _Events = {
        room_call_changeCallType: async function (e) {
            const roomUserList = Room.User.getRoomUserList().concat();
            const me = Room.User.getMe();

            if (e.data.beforeCallType === CALL_TYPE) {
                this.destroy();
                return;
            }

            if (e.data.settings?.callType !== CALL_TYPE) {
                return;
            }

            await this.createStream();

            roomUserList.forEach(ru => {
                if(ru.enterDate < me.enterDate){
                    RoomCall.requestOffer(ru.userId);
                }
            });
        },
        room_user_enter: async function (e) {
            const roomUser = Room.User.getRoomUser(e.data.roomUser);
            if (Room.User.isMe(roomUser)) {
                const list = Room.User.getRoomUserList().concat()
                    .filter(ru => {
                        return (ru.enterDate < roomUser.enterDate);
                    });

                const requestOffer = (list) => {
                    const ru = list.shift();
                    if(!ru) return;

                    RoomCall.requestOffer(ru.userId);
                    if(list.length === 0) return;
                    setTimeout(() => {
                        requestOffer(list);
                    }, 50);
                }
                requestOffer(list);
                return;
            }
        },
        room_user_leave: async function (e) {
            const roomUser = Room.User.getRoomUser(e.data.roomUser) || e.data.roomUser;
            if (!roomUser) {
                return;
            }
            roomUser.conn?.close();
        },
        room_call_requestOffer: async function (e) {
            const roomUser = Room.User.getRoomUser(e.userId);
            if(!roomUser || Room.User.isMe(roomUser)){
                return;
            }
            
            let offer = !roomUser.conn;
            if(e.data?.reason === 'connectionClosed'){
                console.log('connection reset[peer]:', roomUser, e.data.reason);
                offer = true;
            }else if(roomUser.conn){
                if (roomUser.conn.isClosed()) {
                    console.log('connection reset[closed]:', roomUser, e.data);
                    offer = true;
                }
            }
            if(offer){
                await this.createWebRTCConnection(roomUser, {offer: true});
            }else{
                await this.sendOffer({roomUser});
            }
        },
        room_call_offer: async function (e) {
            const roomUser = Room.User.getRoomUser(e.userId);
            if (!roomUser) {
                return;
            }

            let {
                sdp, renew
            } = e.data;

            console.log('#[grm-call] recv offer : userId[%s], sdp[%s]\r\n',
                e.userId, sdp);

            if (roomUser.conn && roomUser.conn.isClosed()) {
                roomUser.conn.close();
                roomUser.conn = null;

                console.warn('conn close already[offer]:', roomUser);
                RoomCall.requestOffer(roomUser.userId, 'connectionClosed');
                return;
            }

            // sendAnswer
            await this.sendAnswer({roomUser, sdp, renew})
        },
        room_call_answer: async function (e) {
            const roomUser = Room.User.getRoomUser(e.userId);
            if (!roomUser) {
                return;
            }

            await roomUser.conn.setRemoteDescription(e.data.sdp);
        },
        room_call_icecandidate: async function (e) {
            const roomUser = Room.User.getRoomUser(e.userId);
            if (!roomUser || !roomUser.conn) {
                console.warn('# roomUser or conn is null :', roomUser, roomUser?.conn);
                return;
            }

            if(roomUser.conn.isClosed()){
                // disconnected 가 아닌 closed 면 connection 재생성
                console.warn('conn close already[ice]:', roomUser);
                roomUser.conn.close();
                roomUser.conn = null;
                RoomCall.requestOffer(roomUser.userId, 'connectionClosed');
                return;
            }
            roomUser.conn.addIceCandidate(e.data.icecandidate);
        },
        room_call_icecandidateComplete: async function (e) {
        },
        room_call_signalStable: async function (e) {
        },
        room_call_screenShare: async function (e) {
            // e.type = e.data.screenShare ? 'room_call_startScreenShare' : 'room_call_stopScreenShare';
            Room.emit(e);
        },
        room_call_changeVideoSenderMaxBitrate: async function (e) {
            const roomUser = Room.User.getRoomUser(e.userId);
            if (!roomUser || !roomUser.conn) {
                console.log('roomUser is null :', roomUser);
                return;
            }

            // const localStream = await RoomCall.Stream.getCurrentLocalStream();
            // const track = localStream && localStream.getVideoTracks()[0];
            const track = await RoomCall.Stream.getSendLocalStreamVideoTrack();
            if (!track){
                return;
            }

            const params = Object.assign({}, e.data);
            if(!params.seq){
                if(params.seq < roomUser.conn._cvsmb_seq){
                    return;
                }
                roomUser.conn._cvsmb_seq = params.seq;
            }

            if (params.videoWidth && params.videoWidth > 0) {
                const settings = track.getSettings();
                let width = settings.width;
                if(settings.facingMode === 'user'){
                    width = settings.height;
                }
                if (
                    params.videoWidth < width
                    && width / params.videoWidth > 2
                ) {
                    // if(params.videoWidth <= 320){
                    //     params.scaleResolutionDownBy = 1;
                    // }else if(track.getSettings().width > 320){
                    //     params.scaleResolutionDownBy = 2;
                    // }
                    params.scaleResolutionDownBy = width / params.videoWidth;
                } else {
                    params.scaleResolutionDownBy = 1;
                }
            }

            if(params.bitrate !== undefined){
                if(params.bitrate < 1){
                    params.active = false;
                }else{
                    params.active = true;
                }
            }

            if(Device.isSafari()){
                if(params.framerate && track._grm_tg){
                    track._grm_tg.changeFPS(params.framerate)
                }
            }

            Object.assign(roomUser._c_data.bw.video, {
                maxBitrate: params.maxBitrate || params.bitrate,
                maxFramerate: params.maxFramerate || params.framerate,
            });
            await roomUser.conn.changeSenderRTCRtpEncodingParameters(track, params);
        },
        room_call_changeSecondVideoSenderMaxBitrate: async function (e) {
            const roomUser = Room.User.getRoomUser(e.userId);
            if (!roomUser || !roomUser.conn) {
                console.log('roomUser is null :', roomUser);
                return;
            }

            const params = Object.assign({}, e.data);
            if(params.bitrate !== undefined){
                if(params.bitrate < 1){
                    params.active = false;
                }else{
                    params.active = true;
                }
            }
            const transArr = roomUser.conn.getPeerConnection().getTransceivers().filter(trans => trans.sender.track?.kind === 'video');
            const trans = transArr[1];
            if(!trans){
                console.log('second transceiver is null');
                return;
            }

            Object.assign(roomUser._c_data.bw.secondVideo, {
                maxBitrate: params.maxBitrate || params.bitrate,
                maxFramerate: params.maxFramerate || params.framerate,
            });
            await roomUser.conn.changeSenderRTCRtpEncodingParameters(trans.sender, params);
        },
        room_call_changeWebinarType: function (e) {
            // 해당 event 없을 경우, room.call.api.js의 event가 덮어 씌워져서 중복 실행됨
        },
        room_call_changeVideoQuality: function (e) {
            // 해당 event 없을 경우, room.call.api.js의 event가 덮어 씌워져서 중복 실행됨
        },
    }

    _CoreEvents = (() => {
        const EVENTS = {
            room_core_visibilitychange: function (e) {
                if (!RoomCall.isP2P()) return;
                if (e.data.hidden) return;

                // background 에서 forground 로 전환 시 peer check. 테스트 필요.
                const me = Room.User.getMe();
                const list = Room.User.getRoomUserList().concat()
                    .filter(ru => {
                        return ru !== me
                            && (!ru.conn || ru.conn.isDisconnected())
                    });

                const requestOffer = (list) => {
                    const ru = list.shift();
                    if(!ru) return;

                    RoomCall.requestOffer(ru.userId, 'connectionClosed');
                    if(list.length === 0) return;
                    setTimeout(() => {
                        requestOffer(list);
                    }, 50);
                }
                requestOffer(list);
            },
            room_core_call_getUserVideoStream: function (e) {
                if (!RoomCall.isP2P()) {
                    return;
                }
                const {roomUser, callback} = e.data;

                if (!roomUser.conn) {
                    return;
                }

                const userId = roomUser.userId;
                const tracks = this.getUserStream({
                    userId, mids: ['0', '1']
                });

                roomUser.conn.streams = roomUser.conn.streams || {};
                const stream = roomUser.conn.streams.video = roomUser.conn.streams.video || new MediaStream();

                tracks.forEach(track => {
                    if (!track) return;
                    if (
                        stream
                            .getTracks()
                            .includes(track)
                    ) {
                        return;
                    }

                    stream.getTracks().filter(t => t.kind === track.kind)
                        .forEach(t => stream.removeTrack(t));
                    stream.addTrack(track);
                });

                callback(stream);
            },
            room_core_call_getUserSecondStream: function (e) {
                if (!RoomCall.isP2P()) {
                    return;
                }
                const {roomUser, callback} = e.data;

                if (!roomUser.conn) {
                    return;
                }

                const userId = roomUser.userId;
                const tracks = this.getUserStream({
                    userId, mids: ['2', '3', '4']
                });
                if (tracks.length > 2) {
                    // console.warn('@@ tracks length :', tracks.length, tracks);
                    const idx = tracks.findIndex(t => !t);
                    if (idx > -1) {
                        tracks.splice(idx, 1);
                    } else {
                        tracks.pop();
                    }
                }

                roomUser.conn.streams = roomUser.conn.streams || {};
                const stream = roomUser.conn.streams.second = roomUser.conn.streams.second || new MediaStream();

                tracks.forEach(track => {
                    if (!track) return;
                    if (
                        stream
                            .getTracks()
                            .includes(track)
                    ) {
                        return;
                    }

                    stream.getTracks().filter(t => t.kind === track.kind)
                        .forEach(t => stream.removeTrack(t));
                    stream.addTrack(track);
                });

                callback(stream);
            },
            room_core_call_changeUserStream: function (e) {
                const {stream, deviceType} = e.data;

                const userId = Room.getRoomData().me.userId;
                RoomCall.setAudioTrackVolumeEvent({
                    userId,
                    stream
                });

                switch (deviceType) {
                    case 'audio':
                    case 'audioinput':
                        if(stream.getAudioTracks().length > 0){
                            RoomCall.addUserStreamTrack(userId, 'audio', stream.getAudioTracks()[0])
                        }
                        break;
                    case 'video':
                    case 'videoinput':
                        if(stream.getVideoTracks().length > 0){
                            RoomCall.addUserStreamTrack(userId, 'video', stream.getVideoTracks()[0])
                        }
                        break;
                }

                const list = Room.User.getRoomUserList();
                list.forEach(roomUser => {
                    if (!roomUser) return;
                    const conn = roomUser.conn;
                    if (!conn) return;

                    switch (deviceType) {
                        case 'audio':
                        case 'audioinput':
                            // conn.replaceFirstAudioSenderTrack(stream.getAudioTracks()[0], CONFIG.mid.primary.audio);
                            // conn.replaceFirstAudioSenderTrack(stream.getAudioTracks()[0]);
                            // break;
                        case 'video':
                        case 'videoinput':
                            // conn.replaceFirstVideoSenderTrack(stream.getVideoTracks()[0], CONFIG.mid.primary.video);
                            // conn.replaceFirstVideoSenderTrack(stream.getVideoTracks()[0]);
                            this._setStream(conn)
                            break;
                    }
                });
            },
            room_core_call_startScreenShare: function (e) {
                const {screenStream, callback} = e.data;
                this.startScreenShare(screenStream, callback);
            },
            room_core_call_stopScreenShare: function (e) {
                this.stopScreenShare();
                Room.emit('room_call_screenShare', e.data);
            },
            room_core_call_changeVideoSenderMaxBitrate: async function (e) {
                await this.changeVideoSenderMaxBitrate(e.data);
            },
            room_core_call_changeSecondVideoSenderMaxBitrate: async function (e) {
                await this.changeSecondVideoSenderMaxBitrate(e.data);
            },
        }

        for (let eventType in EVENTS) {
            const func = EVENTS[eventType];
            EVENTS[eventType] = function (e) {
                if (!RoomCall.isP2P() && e.data?.ignoreCallType !== true) {
                    return;
                }
                return func.apply(this, arguments);
            }
        }

        return EVENTS;
    })()
}

export const RoomCallP2P = new RoomCallP2PApi();